import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { getColorByValue } from "../../../helpers/utils";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import { ChartOptions } from "chart.js/auto";
import { PercentageHistogram } from "../../../types/chartData";
import { forwardRef } from "react";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";

type PercentageHistogramChartProps = {
  height: string | number;
  data1: PercentageHistogram | null;
  attribute: string;
  selection?: string;
  onRendered?: () => void;
};

export const PercentageHistogramChart = forwardRef<
  ChartJSOrUndefined<"bar", number[], string> | undefined,
  PercentageHistogramChartProps
>(
  (
    {
      height,
      data1,
      attribute,
      selection,
      onRendered,
    }: PercentageHistogramChartProps,
    ref
  ) => {
    const { t } = useTranslation();
    const max = 100;
    const histogramData =
      attribute === "MLI"
        ? data1?.mli
        : data1?.pain;

    const labels = Array.from({ length: 10 }, (_, i) => i + 0.5);
    const _data = histogramData?.map((value: number, index: number) => ({
      x: labels[index],
      y: value,
    }));

    const data: any = {
      datasets: [
        {
          label: attribute,
          data: _data,
          backgroundColor: labels.map((_, i) =>
            getColorByValue(i, attribute, true)
          ),
          borderColor: labels.map((_, i) =>
            getColorByValue(i, attribute, true)
          ),
          borderWidth: 1,
          barPercentage: 1,
          categoryPercentage: 1,
        },
      ],
    };

    const options: ChartOptions<"bar"> = {
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "linear",
          offset: false,
          grid: {
            offset: false,
          },
          ticks: {
            stepSize: 1,
          },
          title: {
            display: true,
            text: attribute,
          },
        },
        y: {
          max: max,
        },
      },
      animation: {
        onComplete: () => {
          if (typeof onRendered === "function") {
            onRendered();
          }
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: `   ${selection} ${attribute} ${t("ranges-distribution")}`,
          align: "start",
          padding: {
            top: 5,
            bottom: 15,
          },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          formatter: (_: any, context: any) => {
            const y = context.chart.data.datasets[0].data[context.dataIndex].y;
            return y <= 0 ? "" : `${y}%`;
          },
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              if (!items.length) {
                return "";
              }
              const item = items[0];
              const x = item.parsed.x;
              const labelMin = x - 0.5;
              const labelMax = x + 0.5;
              return `${labelMin} - ${labelMax}`;
            },
          },
        },
      },
    };

    return (
      <div
        style={{
          height: height,
          width: "99%",
        }}
      >
        <Bar
          ref={ref}
          plugins={[ChartDataLabels]}
          data={data}
          options={options}
        />
      </div>
    );
  }
);
